<!--
  @description Fixed bottom-right toast that appears when a new service worker version is
  available. The parent calls the exposed `showPrompt()` method to make the banner visible.
  The user can immediately apply the update (triggers a page reload via `updateSW`) or defer
  it. Hidden by default — only shows after `showPrompt` is called.

  @example
  <PWAUpdatePrompt ref="updatePromptRef" :updateSW="updateSW" />
  // In parent: updatePromptRef.value?.showPrompt()
-->
<template>
  <div v-if="showUpdatePrompt" class="pwa-update-prompt">
    <div class="pwa-update-prompt__content">
      <p>New version available! 🚀</p>
      <div class="pwa-update-prompt__buttons">
        <button @click="updateServiceWorker" class="pwa-update-prompt__update-btn">
          Update now
        </button>
        <button @click="closePrompt" class="pwa-update-prompt__close-btn">Later</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface PWAUpdatePromptProps {
  /** Callback provided by `vite-plugin-pwa`'s `useRegisterSW` composable. Calling it with
   *  `true` forces an immediate page reload to activate the new service worker. */
  updateSW: (reloadPage?: boolean) => Promise<void>
}

const props = defineProps<PWAUpdatePromptProps>()

const showUpdatePrompt = ref(false)

// This function will be called by the parent component when an update is available
const showPrompt = () => {
  showUpdatePrompt.value = true
}

const updateServiceWorker = async () => {
  try {
    await props.updateSW(true)
    showUpdatePrompt.value = false
  } catch (error) {
    console.error('Failed to update service worker:', error)
  }
}

const closePrompt = () => {
  showUpdatePrompt.value = false
}

// Expose methods to parent component
defineExpose({
  showPrompt
})
</script>

<style scoped lang="scss">
.pwa-update-prompt {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  max-width: 320px;
  overflow: hidden;

  &__content {
    padding: 16px;

    p {
      margin: 0 0 12px;
      font-weight: 500;
    }
  }

  &__buttons {
    display: flex;
    gap: 8px;
  }

  &__update-btn,
  &__close-btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  &__update-btn {
    background-color: #4caf50;
    color: white;
  }

  &__close-btn {
    background-color: #f1f1f1;
    color: #333;
  }
}
</style>
